import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, BellOff, User as UserIcon, X, Check, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import GlobalSearch from './GlobalSearch';
import { notificationsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { subscribeToPush } from '../services/webPush';

const Header = ({ title }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const panelRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsAPI.getAll();
      // Item 10 — defensive: always show newest first regardless of source order.
      const list = (res.data || []).slice().sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setNotifications(list);
    } catch (e) { /* silent */ }
  }, []);

  // Poll for overdue checklists + refresh notifications
  useEffect(() => {
    fetchNotifications();
    const triggerCheck = async () => {
      try { await notificationsAPI.checkOverdue(); } catch (e) { /* silent */ }
      try { await notificationsAPI.checkOTAOverdue(); } catch (e) { /* silent */ }
      fetchNotifications();
    };
    triggerCheck();
    const interval = setInterval(triggerCheck, 60000); // Check every 60s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close panel on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setShowPanel(false);
    };
    if (showPanel) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPanel]);

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  const handleDismiss = async (id) => {
    try {
      await notificationsAPI.dismiss(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (e) { /* silent */ }
  };

  const handleNotifClick = (notif) => {
    // Mark as read
    notificationsAPI.markRead(notif.id).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    setShowPanel(false);
    // Phase 16 — Offboarding notifications deep-link directly to the
    // correct step inside the offboarding workflow panel. Other types
    // fall back to the lead page.
    if (notif.type === 'offboarding_event' && notif.lifecycle_id) {
      const stepQs = notif.step_id ? `?focus_step=${encodeURIComponent(notif.step_id)}` : '';
      navigate(`/hotel-lifecycle/offboarding/${notif.lifecycle_id}${stepQs}`);
      return;
    }
    // iter246 — GTM Sales edit-access request notifications deep-link to the
    // account with the request id in the query string so the approver popup
    // auto-opens.
    if (notif.type === 'edit_access_request' && notif.lead_id) {
      const qs = notif.edit_access_request_id ? `?edit_access_req=${encodeURIComponent(notif.edit_access_request_id)}` : '';
      navigate(`/leads/${notif.lead_id}${qs}`);
      return;
    }
    // Default: navigate to the lead
    if (notif.lead_id) {
      navigate(`/leads/${notif.lead_id}`);
    }
  };

  // iter246 — Browser push permission state so we can offer an inline
  // "Enable notifications" prompt from the bell panel.
  const [pushPermission, setPushPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  );
  const enablePush = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await subscribeToPush({ token });
      if (typeof window !== 'undefined' && 'Notification' in window) {
        setPushPermission(Notification.permission);
      }
      if (!res.ok && res.reason && res.reason.startsWith('permission_')) {
        // no-op — user chose denied/dismissed
      }
    } catch (_) { /* silent */ }
  }, []);

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8 sticky top-0 z-30">
      <div className="flex items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
        <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full border border-teal-200">
          Production
        </span>
      </div>
      
      <div className="flex items-center space-x-6">
        <div className="hidden md:block">
          <GlobalSearch />
        </div>

        {/* Notification Bell */}
        <div className="relative" ref={panelRef}>
          <button
            data-testid="notification-bell"
            onClick={() => setShowPanel(!showPanel)}
            className="relative p-2 text-gray-500 hover:text-teal-600 transition-colors"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Panel */}
          {showPanel && (
            <div className="absolute right-0 top-12 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[70vh] flex flex-col" data-testid="notification-panel">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-bold text-gray-800">Notifications</h3>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} className="text-xs text-teal-600 hover:text-teal-700 font-medium">
                    Mark all read
                  </button>
                )}
              </div>
              {/* iter246 — Web Push permission banner. Shown when notifications
                  are supported but permission has not been granted yet. */}
              {pushPermission === 'default' && (
                <div className="px-4 py-3 border-b border-gray-100 bg-teal-50/50 flex items-start gap-3" data-testid="push-permission-banner">
                  <BellOff size={16} className="text-teal-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Enable desktop & mobile notifications</p>
                    <p className="text-xs text-gray-500 mt-0.5">Get CRM alerts even when this tab is closed. Only your session receives them.</p>
                    <button
                      onClick={enablePush}
                      className="mt-2 text-xs font-semibold text-teal-700 hover:text-teal-800"
                      data-testid="enable-push-btn"
                    >Enable notifications</button>
                  </div>
                </div>
              )}
              {pushPermission === 'denied' && (
                <div className="px-4 py-2 border-b border-gray-100 bg-amber-50 text-xs text-amber-700" data-testid="push-permission-denied">
                  Browser notifications are blocked for this site. Enable them in your browser settings to receive alerts when the CRM tab is closed.
                </div>
              )}
              <div className="overflow-y-auto flex-1">
                {(() => {
                  // Item 6 — the panel is the ACTIVE UNREAD view. Once a
                  // notification is marked read it disappears from here
                  // instantly (history is still preserved server-side).
                  const visibleNotifications = notifications.filter(n => !n.read);
                  return visibleNotifications.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <CheckCircle size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : (
                  visibleNotifications.map(notif => (
                    <div
                      key={notif.id}
                      data-testid={`notification-item-${notif.id}`}
                      className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${!notif.read ? 'bg-teal-50/40' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 p-1.5 rounded-lg flex-shrink-0 ${
                          notif.type === 'checklist_overdue' 
                            ? 'bg-amber-100 text-amber-600' 
                            : notif.type === 'ota_overdue'
                            ? 'bg-red-100 text-red-600'
                            : 'bg-teal-100 text-teal-600'
                        }`}>
                          {notif.type === 'checklist_overdue' ? <AlertTriangle size={14} /> : notif.type === 'ota_overdue' ? <Clock size={14} /> : <CheckCircle size={14} />}
                        </div>
                        <div className="flex-1 min-w-0" onClick={() => handleNotifClick(notif)}>
                          <p className="text-sm font-medium text-gray-800 truncate">{notif.hotel_name}</p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                          {notif.pending_fields && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {notif.pending_fields.slice(0, 3).map(f => (
                                <span key={f} className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">{f}</span>
                              ))}
                              {notif.pending_fields.length > 3 && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">+{notif.pending_fields.length - 3}</span>
                              )}
                            </div>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1">{formatTime(notif.created_at)}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDismiss(notif.id); }}
                          className="text-gray-300 hover:text-gray-500 flex-shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                );
                })()}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3 pl-6 border-l border-gray-200">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-gray-900">{user?.name || 'User'}</p>
            <p className="text-xs text-gray-500">{user?.role || 'Agent'}</p>
          </div>
          <div className="h-10 w-10 rounded-full bg-gray-200 overflow-hidden border-2 border-white shadow-sm flex items-center justify-center text-gray-400 cursor-pointer hover:ring-2 hover:ring-teal-500 transition-all">
            {user?.avatar ? (
              <img src={user.avatar} alt="Profile" className="h-full w-full object-cover" />
            ) : (
              <UserIcon size={24} />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
